import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api";
import useEscapeLayer from "../useEscape";
import { alsoOffLine, conflictCopy, shortDate, actorWord, num } from "./listsModel.mjs";

/*
 * The pieces every Lists screen shares: icons, the "Add to list…" menu, the
 * No Contact confirms (frame 4), the undo toast (frame 3b), and the one add
 * flow that knows a 409 means "someone here is on No Contact".
 */

/** A key press while typing in a field is the field's, not a shortcut. A ticked
 * checkbox keeps focus, and is not typing: N right after ticking rows has to work. */
export function isTyping(e) {
  const t = e.target;
  if (!t || !t.closest) return false;
  if (t.closest("textarea, select, [contenteditable=\"true\"]")) return true;
  return t.tagName === "INPUT" && !["checkbox", "radio", "button", "submit"].includes(t.type);
}

const svg = (children, { size = 14, sw = 2.4 } = {}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
export const Icon = {
  ban: (p) => svg(<><circle cx="12" cy="12" r="9" /><path d="M5.6 5.6l12.8 12.8" /></>, p),
  lock: (p) => svg(<><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>, { size: 11, sw: 2.6, ...p }),
  plus: (p) => svg(<path d="M12 5v14M5 12h14" />, { sw: 3, ...p }),
  search: (p) => svg(<><circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" /></>, { size: 15, ...p }),
  pair: (p) => svg(<><circle cx="8" cy="9" r="3.5" /><circle cx="16" cy="9" r="3.5" /><path d="M2.5 20c.6-3 2.8-5 5.5-5M21.5 20c-.6-3-2.8-5-5.5-5M10 15.5h4" /></>, { size: 18, sw: 2, ...p }),
  close: (p) => svg(<path d="M6 6l12 12M18 6L6 18" />, { size: 16, ...p }),
};

export function BuiltIn() {
  return <span className="lst-pill" title="Built-in list"><Icon.lock />Built-in</span>;
}

/** The lists, loaded once per screen. */
export function useLists() {
  const [state, setState] = useState({ lists: [], peopleOnAny: 0, reviewCount: 0, loaded: false });
  const load = async () => {
    const data = await api("/lists");
    setState({ ...data, loaded: true });
    return data;
  };
  useEffect(() => {
    load().catch(() => setState((s) => ({ ...s, loaded: true })));
  }, []);
  return [state, load];
}

/**
 * "Add to list…": every ordinary list, filterable, with how many of the
 * selection each already holds when `alreadyOn` is given; "+ New list from
 * these N" at the foot.
 */
export function AddToListMenu({ lists, exceptId = null, alreadyOn = null, count, onPick, onNew, onClose, style }) {
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEscapeLayer(true, onClose);
  useEffect(() => {
    const away = (e) => ref.current && !ref.current.contains(e.target) && onClose();
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [onClose]);
  const shown = lists.filter((l) => l.kind === "standard" && l.id !== exceptId && l.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="lst-pop" role="menu" aria-label={`Add ${count} to a list`} ref={ref} style={style}>
      <input type="search" aria-label="Find a list" placeholder="Find a list" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
      {shown.map((l, i) => (
        <button key={l.id} type="button" role="menuitem" className={`lst-opt${i === 0 ? " is-hl" : ""}`} onClick={() => onPick(l)}>
          {l.name}
          {alreadyOn && alreadyOn[l.id] > 0 && <small>{alreadyOn[l.id]} already on</small>}
        </button>
      ))}
      {shown.length === 0 && <div className="lst-note" style={{ padding: "6px 8px" }}>No other lists yet.</div>}
      {onNew && (
        <>
          <div className="lst-pop__div" />
          <button type="button" role="menuitem" className="lst-opt" onClick={onNew}>+ New list from {count === 1 ? "this person" : `these ${num(count)}`}</button>
        </>
      )}
    </div>
  );
}

/** Frame 4: the single and the bulk No Contact confirm. */
export function ConflictDialog({ conflict, listName, onCancel, onAddRest, onTakeAllOff, busy }) {
  const copy = conflictCopy({ conflicts: conflict.conflicts, total: conflict.total, listName });
  const [choice, setChoice] = useState(copy.rest > 0 ? "rest" : "all");
  useEscapeLayer(true, onCancel);
  const one = conflict.conflicts[0];
  return (
    <div className="crm-modal__backdrop" onClick={onCancel}>
      <section className="lst-dlg" role="dialog" aria-modal="true" aria-labelledby="lst-dlg-t" onClick={(e) => e.stopPropagation()}>
        <div><span className="lst-nc-tag"><Icon.ban size={12} />No Contact</span></div>
        <h2 className="lst-dlg__title" id="lst-dlg-t">{copy.title}</h2>
        <p className="lst-dlg__sub">{copy.single ? copy.sub : <>{copy.sub.split(listName)[0]}<b>{listName}</b>{copy.sub.split(listName)[1]}</>}</p>
        {copy.single ? (
          <div className="lst-muted-box">
            <div className="lst-note">
              <b style={{ color: "var(--crm-black)" }}>On No Contact{one.since ? ` since ${shortDate(one.since)}` : ""}</b>
              {one.by ? ` · added ${actorWord(one.by)}` : ""}
            </div>
            {one.would_restore?.length > 0 && (
              <div className="lst-note">
                Coming off No Contact also puts {copy.first} back on the lists {copy.first} was on:{" "}
                {one.would_restore.map((n) => <span key={n} className="lst-pill" style={{ marginRight: 4 }}>{n}</span>)}
              </div>
            )}
          </div>
        ) : (
          <>
            <div style={{ border: "var(--crm-border)", borderRadius: 6 }}>
              {conflict.conflicts.map((c) => (
                <div key={c.personId} className="lst-who">
                  <b style={{ flex: "0 0 150px" }}>{c.name || c.email}</b>
                  <span className="lst-note" style={{ flex: 1 }}>{[c.company, c.job_title].filter(Boolean).join(" · ")}</span>
                  <span className="lst-note">{[c.since && `since ${shortDate(c.since)}`, c.by && actorWord(c.by).replace(/^by /, "")].filter(Boolean).join(" · ")}</span>
                </div>
              ))}
            </div>
            {copy.rest > 0 && (
              <label className={`lst-choice${choice === "rest" ? " is-on" : ""}`}>
                <input type="radio" name="lst-nc" checked={choice === "rest"} onChange={() => setChoice("rest")} />
                <span><b>{copy.restLabel}</b><br /><span className="lst-note">{copy.restNote}</span></span>
              </label>
            )}
            <label className={`lst-choice${choice === "all" ? " is-on" : ""}`}>
              <input type="radio" name="lst-nc" checked={choice === "all"} onChange={() => setChoice("all")} />
              <span><b>{copy.allLabel}</b><br /><span className="lst-note">They also go back to the lists they were on before.</span></span>
            </label>
          </>
        )}
        <div className="lst-dlg__acts">
          <button className="crm-btn crm-btn--secondary" type="button" onClick={onCancel} disabled={busy}>
            {copy.single ? copy.keep : <>Cancel <span className="lst-kbd" style={{ marginLeft: 6 }}>Esc</span></>}
          </button>
          <button
            className="crm-btn crm-btn--dark"
            type="button"
            disabled={busy}
            autoFocus
            onClick={() => (copy.single || choice === "all" ? onTakeAllOff() : onAddRest())}
          >
            {copy.single ? copy.go : choice === "rest" ? copy.goRest : copy.goAll}
          </button>
        </div>
      </section>
    </div>
  );
}

/** Frame 3b: what just happened, with Undo (Z) for eight seconds. */
export function UndoToast({ toast, onDone }) {
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(onDone, 8000);
    return () => clearTimeout(t);
  }, [toast, onDone]);
  const undo = useMemo(
    () => async () => {
      if (!toast?.undo || busy) return;
      setBusy(true);
      try {
        await toast.undo();
      } finally {
        setBusy(false);
        onDone();
      }
    },
    [toast, busy, onDone]
  );
  useEffect(() => {
    if (!toast?.undo) return undefined;
    const key = (e) => {
      if (isTyping(e)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "z" || e.key === "Z") {
        e.preventDefault();
        undo();
      }
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [toast, undo]);
  if (!toast) return null;
  return (
    <div className="lst-toast" role="status">
      {toast.icon !== false && <Icon.ban size={20} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="lst-toast__title">{toast.title}</div>
        {toast.sub && <div className="lst-toast__sub">{toast.sub}</div>}
      </div>
      {toast.undo && (
        <button type="button" className="lst-bbtn" onClick={undo} disabled={busy}>
          Undo <span className="lst-kbd">Z</span>
        </button>
      )}
    </div>
  );
}

/**
 * Put people on a list, walking a 409 through the confirm. Resolves to the
 * add's result, or null when the person cancelled.
 *
 *   ask(conflict) -> Promise<"rest" | "all" | null>   shows ConflictDialog
 */
export async function addToList(list, personIds, ask) {
  const path = `/lists/${list.id}/members`;
  try {
    return await api(path, { method: "POST", body: JSON.stringify({ person_ids: personIds }) });
  } catch (err) {
    if (err.status !== 409 || err.body?.code !== "on_no_contact") throw err;
    const answer = await ask(err.body);
    if (!answer) return null;
    if (answer === "all") return api(path, { method: "POST", body: JSON.stringify({ person_ids: personIds, override: true }) });
    const blocked = new Set(err.body.conflicts.map((c) => c.personId));
    const rest = personIds.filter((id) => !blocked.has(id));
    if (!rest.length) return { added: 0, addedIds: [] };
    return api(path, { method: "POST", body: JSON.stringify({ person_ids: rest }) });
  }
}

/** Move to No Contact, and the toast that can undo it. */
export async function moveToNoContact(personIds, { exceptListId = null } = {}) {
  const res = await api("/lists/no-contact/members", { method: "POST", body: JSON.stringify({ person_ids: personIds }) });
  const n = personIds.length;
  return {
    res,
    toast: {
      title: `${num(n)} moved to No Contact`.replace(/^1 moved/, "1 person moved"),
      sub: alsoOffLine((res.removedFrom || []).filter((r) => r.listId !== exceptListId)),
      undo: res.addedIds?.length
        ? () => api("/lists/no-contact/members/remove", { method: "POST", body: JSON.stringify({ person_ids: res.addedIds }) })
        : null,
    },
  };
}

/** The confirm as a promise, for addToList: [element, ask]. */
export function useConflictAsk(listName) {
  const [pending, setPending] = useState(null);
  const ask = (conflict) => new Promise((resolve) => setPending({ conflict, resolve }));
  const finish = (answer) => {
    pending?.resolve(answer);
    setPending(null);
  };
  const el = pending ? (
    <ConflictDialog
      conflict={pending.conflict}
      listName={pending.listName || listName}
      onCancel={() => finish(null)}
      onAddRest={() => finish("rest")}
      onTakeAllOff={() => finish("all")}
    />
  ) : null;
  const askFor = (name) => (conflict) => new Promise((resolve) => setPending({ conflict, resolve, listName: name }));
  return { el, ask, askFor };
}

/** "New list" (index) and "+ New list from these 12" (bulk bar). */
export function NewListModal({ onClose, onCreated, count = 0 }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  useEscapeLayer(true, onClose);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const { list } = await api("/lists", { method: "POST", body: JSON.stringify({ name, description: description.trim() || null }) });
      await onCreated(list);
    } catch (e2) {
      setErr(e2.message);
      setBusy(false);
    }
  };
  return (
    <div className="crm-modal__backdrop" onClick={onClose}>
      <form className="crm-modal" onClick={(e) => e.stopPropagation()} onSubmit={save}>
        <div className="crm-modal__head"><h2>{count ? `New list from ${num(count)} ${count === 1 ? "person" : "people"}` : "New list"}</h2></div>
        <div className="crm-modal__body">
          <label className="crm-field">
            <span className="crm-label">Name</span>
            <input className="crm-input" value={name} onChange={(e) => setName(e.target.value)} autoFocus maxLength={120} />
          </label>
          <label className="crm-field">
            <span className="crm-label">What it is for</span>
            <input className="crm-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" />
          </label>
          {err && <p className="crm-login__err">{err}</p>}
          <div className="lst-dlg__acts">
            <button type="button" className="crm-btn crm-btn--secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="crm-btn crm-btn--primary" disabled={busy || !name.trim()}>Create list</button>
          </div>
        </div>
      </form>
    </div>
  );
}
